import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './StaticPage.css';

function PrivacyPolicyPage() {
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="static-page">
      <nav className={`nav ${scrolled ? 'nav-scrolled' : ''}`}>
        <div className="nav-container">
          <a href="/" className="nav-logo" aria-label="CerebralMachines home"><img src="/CMLogo.png" alt="CerebralMachines" /></a>
          <div className="nav-links">
            <a href="/">Home</a>
            <a href="/platform">Platform</a>
            <a href="/how-it-works">How it Works</a>
            <a href="/use-cases">Use Cases</a>
            <a href="/consulting">Consulting</a>
          </div>
          <div className="nav-actions">
            <button className="btn-ghost" onClick={() => navigate('/login')}>Sign In</button>
            <button className="btn-primary" onClick={() => navigate('/request-demo')}>Request a Demo</button>
          </div>
        </div>
      </nav>

      <section className="sp-hero">
        <div className="sp-container">
          <h1 className="sp-hero-title">Privacy Policy</h1>
          <p className="sp-hero-text">
            Last updated: March 14, 2026
          </p>
        </div>
      </section>

      <section className="sp-section sp-legal">
        <div className="sp-container">
          <h3>1. Introduction</h3>
          <p className="sp-text">
            CerebralMachines, Inc. ("CerebralMachines", "we", "us", or "our") is committed to protecting the
            privacy of our users, customers, and website visitors. This Privacy Policy describes how we collect,
            use, disclose, and safeguard your information when you visit our website, use our platform, or
            engage with our services.
          </p>

          <h3>2. Information We Collect</h3>
          <p className="sp-text">
            We may collect the following types of information:
          </p>
          <ul className="sp-list">
            <li><strong>Account Information:</strong> Name, email address, company name, job title, phone number, and other details you provide during registration or demo requests.</li>
            <li><strong>Usage Data:</strong> Information about how you interact with our platform, including pages visited, features used, and session duration.</li>
            <li><strong>Technical Data:</strong> IP address, browser type, operating system, device information, and cookies.</li>
            <li><strong>Customer Data:</strong> Data you upload or process through the CerebralMachines platform as part of your enterprise workflows.</li>
          </ul>

          <h3>3. How We Use Your Information</h3>
          <p className="sp-text">We use the information we collect to:</p>
          <ul className="sp-list">
            <li>Provide, maintain, and improve our platform and services</li>
            <li>Process your account registration and manage your subscription</li>
            <li>Respond to your inquiries, demo requests, and support needs</li>
            <li>Send relevant communications about product updates and features</li>
            <li>Ensure platform security, detect fraud, and prevent abuse</li>
            <li>Comply with legal obligations and enforce our terms</li>
          </ul>

          <h3>4. Data Sharing and Disclosure</h3>
          <p className="sp-text">
            We do not sell your personal information. We may share data with:
          </p>
          <ul className="sp-list">
            <li><strong>Service Providers:</strong> Third-party vendors who assist in operating our platform (hosting, analytics, support tools), bound by confidentiality obligations.</li>
            <li><strong>Legal Compliance:</strong> When required by law, regulation, or legal process.</li>
            <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets.</li>
          </ul>

          <h3>5. Data Security</h3>
          <p className="sp-text">
            We implement industry-standard security measures including encryption in transit and at rest,
            access controls, audit logging, and regular security assessments. Our platform is designed to
            meet the requirements of HIPAA, SOC 2, and GDPR compliance frameworks.
          </p>

          <h3>6. Data Retention</h3>
          <p className="sp-text">
            We retain your information for as long as your account is active or as needed to provide services.
            We will delete or anonymize your data upon request, subject to legal retention requirements.
          </p>

          <h3>7. Your Rights</h3>
          <p className="sp-text">Depending on your jurisdiction, you may have the right to:</p>
          <ul className="sp-list">
            <li>Access, correct, or delete your personal data</li>
            <li>Object to or restrict certain processing activities</li>
            <li>Request data portability</li>
            <li>Withdraw consent where processing is based on consent</li>
          </ul>

          <h3>8. Cookies</h3>
          <p className="sp-text">
            We use cookies and similar technologies to improve your experience, analyze usage patterns,
            and support our marketing efforts. You can manage cookie preferences through your browser settings.
          </p>

          <h3>9. Changes to This Policy</h3>
          <p className="sp-text">
            We may update this Privacy Policy from time to time. We will notify you of material changes by
            posting the updated policy on our website with a revised "Last updated" date.
          </p>

          <h3>10. Contact Us</h3>
          <p className="sp-text">
            If you have questions about this Privacy Policy or our data practices, please contact us at:
          </p>
          <p className="sp-emphasis">privacy@cerebralmachines.ai</p>
        </div>
      </section>

      <footer className="footer">
        <div className="footer-container">
          <div className="footer-main">
            <div className="footer-brand">
              <a href="/" className="footer-logo" aria-label="CerebralMachines home"><img src="/CMLogo.png" alt="CerebralMachines" /></a>
              <p className="footer-tagline">Building the cognitive backbone for enterprise AI.</p>
            </div>
            <div className="footer-links">
              <div className="footer-column">
                <h4>Product</h4>
                <ul>
                  <li><a href="/platform">Platform</a></li>
                  <li><a href="/how-it-works">How it Works</a></li>
                  <li><a href="/use-cases">Use Cases</a></li>
                  <li><a href="/consulting">Consulting</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Company</h4>
                <ul>
                  <li><a href="/about">About</a></li>
                  <li><a href="/team">Team</a></li>
                  <li><a href="/careers">Careers</a></li>
                  <li><a href="/contact">Contact</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Legal</h4>
                <ul>
                  <li><a href="/privacy">Privacy Policy</a></li>
                  <li><a href="/terms">Terms of Service</a></li>
                  <li><a href="/security">Security</a></li>
                </ul>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2026 CerebralMachines. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default PrivacyPolicyPage;
